
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  AlertTriangle,
  FileCheck2,
  FileText,
  RefreshCw,
  ShieldCheck,
} from "lucide-react";

import api from "../axiosConfig";
import { getStoredUser } from "../lib/authStorage";
import "../styles/FiscalCertification.css";

const DOCUMENT_TYPES = ["FT", "FS", "FR", "NC", "ND"];

function localDateToday() {
  const now = new Date();
  const local = new Date(
    now.getTime() - now.getTimezoneOffset() * 60000
  );
  return local.toISOString().slice(0, 10);
}

function apiIsTest() {
  const hostname =
    typeof window !== "undefined"
      ? String(window.location.hostname || "").trim().toLowerCase()
      : "";

  if (hostname === "test-app.loadevo.com") return true;

  return String(api?.defaults?.baseURL || "")
    .trim()
    .toLowerCase()
    .includes("test-api.loadevo.com");
}

function certificationUserAllowed() {
  const user = getStoredUser();
  const role = String(user?.role || "").trim().toLowerCase();

  return (
    apiIsTest() &&
    ["accountant", "admin", "dispatcher"].includes(role)
  );
}

function deepClone(value) {
  return JSON.parse(JSON.stringify(value));
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("pt-PT");
}

function draftDocumentType(draft) {
  return String(
    draft?.payload?.documentType ||
      draft?.payload?.type ||
      draft?.payload?.document?.type ||
      ""
  )
    .trim()
    .toUpperCase();
}

function draftDescription(draft) {
  return (
    draft?.payload?.lines?.[0]?.description ||
    draft?.payload?.description ||
    draft?.payload?.documentDescription ||
    "Documento de formação"
  );
}

function documentType(document) {
  return String(
    document?.documentType ||
      document?.invoiceType ||
      document?.type ||
      ""
  )
    .trim()
    .toUpperCase();
}

function documentNumber(document) {
  return (
    document?.documentNumber ||
    document?.invoiceNo ||
    document?.documentNo ||
    "—"
  );
}

function documentStatus(document) {
  return document?.status || document?.documentStatus || "—";
}

function documentAtcud(document) {
  return (
    document?.atcud ||
    document?.ATCUD ||
    document?.atCud ||
    "—"
  );
}

function documentHash(document) {
  return document?.hash || document?.documentHash || "—";
}

function statusPillClass(value) {
  const normalized = String(value || "").trim().toUpperCase();

  if (normalized.includes("CANCEL")) {
    return "fiscal-certification-status-pill is-cancelled";
  }

  if (normalized === "ISSUED") {
    return "fiscal-certification-status-pill is-issued";
  }

  if (normalized === "DRAFT") {
    return "fiscal-certification-status-pill is-draft";
  }

  return "fiscal-certification-status-pill";
}

function operationError(requestError, fallback) {
  const code = String(requestError?.response?.data?.error || "");
  const message = String(
    requestError?.response?.data?.message || ""
  ).trim();

  if (code === "certification_issuance_disarmed") {
    return "A emissão fiscal AT está desarmada. Ative o latch controlado antes do walkthrough de emissão.";
  }

  if (code === "certification_cancellation_disarmed") {
    return "O cancelamento fiscal AT está desarmado. Ative o latch controlado antes do walkthrough de cancelamento.";
  }

  if (code === "certification_gateway_unavailable") {
    return "O serviço fiscal de certificação AT não está disponível.";
  }

  if (code === "certification_access_denied") {
    return "Este utilizador não tem acesso ao ambiente de certificação AT.";
  }

  return message || fallback;
}

export default function FiscalCertification() {
  const allowed = useMemo(() => certificationUserAllowed(), []);

  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [state, setState] = useState({
    environment: "",
    companyId: null,
    issuanceArmed: false,
    cancellationArmed: false,
    operationAuthorizations: {
      issue: null,
      cancel: null,
    },
    drafts: [],
    documents: [],
    fiscalDocumentOperations: "",
  });

  const [selectedDraftId, setSelectedDraftId] = useState("");
  const [selectedDocumentId, setSelectedDocumentId] = useState("");
  const [newDocumentType, setNewDocumentType] = useState("FT");
  const [issueDate, setIssueDate] = useState(localDateToday());

  const [newDescription, setNewDescription] = useState(
    "Serviço de transporte — formação AT"
  );

  const [originalDocumentId, setOriginalDocumentId] = useState("");

  const [rectificationReason, setRectificationReason] = useState(
    "Retificação de formação AT"
  );

  const [editDescription, setEditDescription] = useState("");

  const [cancellationReason, setCancellationReason] = useState(
    "Cancelamento de formação AT"
  );

  const [originalNotDelivered, setOriginalNotDelivered] =
    useState(true);

  const [saftDate, setSaftDate] = useState(localDateToday());

  const load = useCallback(async () => {
    if (!allowed) return;

    setLoading(true);
    setError("");

    try {
      const { data } = await api.get(
        "/certification/fiscal/documents"
      );

      if (!data || data.ok !== true) {
        throw new Error("invalid_certification_response");
      }

      setState({
        environment: data.environment || "",
        companyId: data.companyId ?? null,
        issuanceArmed: data.issuanceArmed === true,
        cancellationArmed: data.cancellationArmed === true,
        operationAuthorizations:
          data.operationAuthorizations &&
          typeof data.operationAuthorizations === "object"
            ? data.operationAuthorizations
            : { issue: null, cancel: null },
        drafts: Array.isArray(data.drafts) ? data.drafts : [],
        documents: Array.isArray(data.documents)
          ? data.documents
          : [],
        fiscalDocumentOperations:
          data.fiscalDocumentOperations || "",
      });
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível carregar os dados do ambiente de certificação."
        )
      );
    } finally {
      setLoading(false);
    }
  }, [allowed]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedDraft = useMemo(
    () =>
      state.drafts.find(
        draft => String(draft.id) === String(selectedDraftId)
      ) || null,
    [selectedDraftId, state.drafts]
  );

  const selectedDocument = useMemo(
    () =>
      state.documents.find(
        document =>
          String(document.id) === String(selectedDocumentId)
      ) || null,
    [selectedDocumentId, state.documents]
  );

  const currentTemplate = useMemo(() => {
    const candidates = [...state.drafts].reverse();

    const completeCustomer = candidates.find(draft => {
      const customer = draft?.payload?.customer || {};

      return (
        String(
          customer.addressDetail || customer.address || ""
        ).trim() &&
        String(customer.city || "").trim() &&
        String(customer.postalCode || "").trim()
      );
    });

    const sameTypeTemplate = candidates.find(
      draft =>
        draftDocumentType(draft) === newDocumentType &&
        Array.isArray(draft?.payload?.lines) &&
        draft.payload.lines.length > 0
    );

    if (!sameTypeTemplate) return null;
    if (newDocumentType === "FS") return sameTypeTemplate;
    if (!completeCustomer) return null;

    return {
      ...sameTypeTemplate,
      payload: {
        ...sameTypeTemplate.payload,
        customer: deepClone(completeCustomer.payload.customer),
      },
    };
  }, [newDocumentType, state.drafts]);

  const originalCandidates = useMemo(
    () =>
      state.documents.filter(document => {
        const type = documentType(document);
        const status = String(documentStatus(document))
          .trim()
          .toUpperCase();

        return (
          ["FT", "FS", "FR"].includes(type) &&
          !status.includes("CANCEL")
        );
      }),
    [state.documents]
  );

  useEffect(() => {
    if (selectedDraft) {
      setEditDescription(draftDescription(selectedDraft));
    } else {
      setEditDescription("");
    }
  }, [selectedDraft]);

  useEffect(() => {
    if (!originalDocumentId && originalCandidates.length > 0) {
      setOriginalDocumentId(String(originalCandidates[0].id));
    }
  }, [originalCandidates, originalDocumentId]);

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  const environmentOk = state.environment === "AT_CERTIFICATION";

  const isRectification =
    newDocumentType === "NC" || newDocumentType === "ND";

  const selectedDraftIsEditable = selectedDraft?.status === "DRAFT";

  const selectedDocumentIsCancelled = String(
    documentStatus(selectedDocument)
  )
    .toUpperCase()
    .includes("CANCEL");

  async function createDraft(event) {
    event.preventDefault();
    setError("");
    setNotice("");

    if (!currentTemplate) {
      setError(
        `Não existe um fixture/template ${newDocumentType} no ambiente AT.`
      );
      return;
    }

    if (!issueDate) {
      setError("Indique a data do documento.");
      return;
    }

    if (isRectification && !originalDocumentId) {
      setError(
        "Selecione o documento original para a retificação."
      );
      return;
    }

    if (
      isRectification &&
      rectificationReason.trim().length < 3
    ) {
      setError("Indique um motivo de retificação.");
      return;
    }

    setBusyAction("create");

    try {
      const payload = deepClone(currentTemplate.payload);

      payload.companyId = 910001;
      payload.documentType = newDocumentType;
      payload.issueDate = issueDate;
      payload.chronologyConfirmation = null;

      if (
        Object.prototype.hasOwnProperty.call(
          payload,
          "taxPointDate"
        )
      ) {
        payload.taxPointDate = issueDate;
      }

      if (Array.isArray(payload.lines) && payload.lines[0]) {
        payload.lines[0].description =
          newDescription.trim() ||
          `Formação AT ${newDocumentType}`;
      }

      if (isRectification) {
        payload.originalDocumentId = Number(originalDocumentId);
        payload.rectificationReason =
          rectificationReason.trim();
      } else {
        delete payload.originalDocumentId;
        delete payload.rectificationReason;
        delete payload.rectifiedFields;
      }

      const { data } = await api.post(
        "/certification/fiscal/documents/drafts",
        payload
      );

      if (!data || data.ok !== true || !data.draft) {
        throw new Error("invalid_draft_response");
      }

      setSelectedDraftId(data.draft.id);
      setNotice(
        `${data.draft.id} criado para ${newDocumentType}.`
      );

      await load();
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível criar o DRAFT de formação."
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function saveDraftDescription() {
    if (!selectedDraftIsEditable) return;

    if (editDescription.trim().length < 3) {
      setError("A descrição deve ter pelo menos 3 caracteres.");
      return;
    }

    setError("");
    setNotice("");
    setBusyAction("edit");

    try {
      const { data } = await api.put(
        `/certification/fiscal/documents/drafts/${selectedDraft.id}`,
        { description: editDescription.trim() }
      );

      if (!data || data.ok !== true) {
        throw new Error("invalid_draft_update_response");
      }

      setNotice(`${selectedDraft.id} atualizado.`);
      await load();
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível atualizar o DRAFT."
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function authorizeNextCertificationOperation(operation) {
    const normalized =
      operation === "CANCEL" ? "CANCEL" : "ISSUE";

    const label =
      normalized === "CANCEL" ? "cancelamento" : "emissão";

    const confirmed = window.confirm(
      `Autorizar apenas a próxima ${label} fiscal de formação durante até 5 minutos?`
    );

    if (!confirmed) return;

    setError("");
    setNotice("");
    setBusyAction(
      normalized === "CANCEL"
        ? "authorize-cancel"
        : "authorize-issue"
    );

    try {
      const { data } = await api.post(
        "/certification/fiscal/authorizations",
        {
          operation: normalized,
          confirmation:
            normalized === "CANCEL"
              ? "AUTHORIZE_NEXT_CANCEL"
              : "AUTHORIZE_NEXT_ISSUE",
        }
      );

      if (
        !data ||
        data.ok !== true ||
        data.armed !== true ||
        data.source !== "TESTER_ONE_SHOT"
      ) {
        throw new Error(
          "invalid_certification_authorization_response"
        );
      }

      setNotice(
        normalized === "CANCEL"
          ? "Próximo cancelamento autorizado. A autorização é one-shot e expira em até 5 minutos."
          : "Próxima emissão autorizada. A autorização é one-shot e expira em até 5 minutos."
      );

      await load();
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          `Não foi possível autorizar a próxima ${label}.`
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function revokeCertificationOperation(operation) {
    const normalized =
      operation === "CANCEL" ? "CANCEL" : "ISSUE";

    const label =
      normalized === "CANCEL" ? "cancelamento" : "emissão";

    setError("");
    setNotice("");
    setBusyAction(
      normalized === "CANCEL" ? "revoke-cancel" : "revoke-issue"
    );

    try {
      const { data } = await api.delete(
        `/certification/fiscal/authorizations/${normalized}`
      );

      if (!data || data.ok !== true) {
        throw new Error(
          "invalid_certification_authorization_revoke_response"
        );
      }

      setNotice(`Autorização de ${label} desarmada.`);
      await load();
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          `Não foi possível desarmar a autorização de ${label}.`
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function issueSelectedDraft() {
    if (!selectedDraftIsEditable) return;

    if (!state.issuanceArmed) {
      setError(
        "A emissão AT está atualmente desarmada. O DRAFT não foi emitido."
      );
      return;
    }

    setError("");
    setNotice("");
    setBusyAction("issue");

    try {
      const { data } = await api.post(
        "/certification/fiscal/documents",
        { draftId: selectedDraft.id }
      );

      if (!data || data.ok !== true) {
        throw new Error("invalid_issue_response");
      }

      setNotice(`${selectedDraft.id} emitido com sucesso.`);
      await load();
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível emitir o documento."
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function cancelSelectedDocument() {
    if (!selectedDocument || selectedDocumentIsCancelled) return;

    if (!state.cancellationArmed) {
      setError(
        "O cancelamento AT está atualmente desarmado. O documento não foi cancelado."
      );
      return;
    }

    if (cancellationReason.trim().length < 3) {
      setError("Indique um motivo de cancelamento.");
      return;
    }

    if (!originalNotDelivered) {
      setError(
        "Confirme que o original não foi entregue ao cliente para este fluxo de formação."
      );
      return;
    }

    setError("");
    setNotice("");
    setBusyAction("cancel");

    try {
      const { data } = await api.post(
        `/certification/fiscal/documents/${selectedDocument.id}/cancel`,
        {
          cancellationReason: cancellationReason.trim(),
          originalNotDeliveredToCustomer: true,
        }
      );

      if (!data || data.ok !== true) {
        throw new Error("invalid_cancel_response");
      }

      setNotice(
        `Documento ${documentNumber(selectedDocument)} cancelado.`
      );
      await load();
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível cancelar o documento."
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function openSelectedPdf() {
    if (!selectedDocument) return;

    setError("");
    setNotice("");
    setBusyAction("pdf");

    try {
      const response = await api.get(
        `/certification/fiscal/documents/${selectedDocument.id}/pdf`,
        { responseType: "blob" }
      );

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: "application/pdf",
            });

      const url = URL.createObjectURL(blob);

      const opened = window.open(
        url,
        "_blank",
        "noopener,noreferrer"
      );

      if (!opened) {
        const link = document.createElement("a");
        link.href = url;
        link.download = `fiscal-${selectedDocument.id}.pdf`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

      window.setTimeout(() => URL.revokeObjectURL(url), 60000);

      setNotice(
        `PDF de ${documentNumber(selectedDocument)} preparado.`
      );
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível obter o PDF."
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  async function exportSaft() {
    if (!/^2026-\d{2}-\d{2}$/.test(saftDate)) {
      setError("A data SAF-T deve pertencer a 2026.");
      return;
    }

    setError("");
    setNotice("");
    setBusyAction("saft");

    try {
      const response = await api.get(
        "/certification/fiscal/saft",
        {
          params: { dateCreated: saftDate },
          responseType: "blob",
        }
      );

      const blob =
        response.data instanceof Blob
          ? response.data
          : new Blob([response.data], {
              type: "application/xml",
            });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `SAFT_PT_910001_${saftDate}.xml`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(url);
      setNotice(`SAF-T exportado até ${saftDate}.`);
    } catch (requestError) {
      setError(
        operationError(
          requestError,
          "Não foi possível exportar o SAF-T."
        )
      );
    } finally {
      setBusyAction("");
    }
  }

  return (
    <div className="fiscal-certification-page">
      <div className="fiscal-certification-banner">
        <div className="fiscal-certification-banner__icon">
          <ShieldCheck size={22} />
        </div>

        <div>
          <strong>AMBIENTE AT — TEST / FORMAÇÃO</strong>
          <span>
            Dados exclusivamente sintéticos. Esta área não
            constitui faturação de produção.
          </span>
        </div>

        <span className="fiscal-certification-banner__badge">
          TEST · FORMAÇÃO
        </span>
      </div>

      <header className="fiscal-certification-header">
        <div className="fiscal-certification-header__identity">
          <span
            className="fiscal-certification-header__icon"
            aria-hidden="true"
          >
            <FileCheck2 size={24} />
          </span>

          <div>
            <div className="fiscal-certification-eyebrow">
              Autoridade Tributária · Ambiente de conformidade
            </div>

            <h1>Certificação Fiscal</h1>

            <p>
              Walkthrough de formação para FT, FS, FR, NC, ND,
              cancelamento, PDF e SAF-T.
            </p>
          </div>
        </div>

        <button
          type="button"
          className="fiscal-certification-refresh"
          onClick={load}
          disabled={loading || Boolean(busyAction)}
        >
          <RefreshCw size={17} />
          Atualizar
        </button>
      </header>

      {error && (
        <div className="fiscal-certification-error">
          <AlertTriangle size={18} />
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="fiscal-certification-notice">
          <FileCheck2 size={18} />
          <span>{notice}</span>
        </div>
      )}

      <section className="fiscal-certification-status-grid">
        <div className="fiscal-certification-status-card">
          <span>Ambiente</span>
          <strong className={environmentOk ? "is-ok" : ""}>
            {state.environment ||
              (loading ? "A carregar…" : "Não confirmado")}
          </strong>
        </div>

        <div className="fiscal-certification-status-card">
          <span>Company AT</span>
          <strong>{state.companyId ?? "—"}</strong>
        </div>

        <div className="fiscal-certification-status-card">
          <span>Emissão</span>
          <strong
            className={
              state.issuanceArmed ? "is-ok" : "is-warning"
            }
          >
            {state.issuanceArmed ? "ARMADA" : "DESARMADA"}
          </strong>
        </div>

        <div className="fiscal-certification-status-card">
          <span>Cancelamento</span>
          <strong
            className={
              state.cancellationArmed ? "is-ok" : "is-warning"
            }
          >
            {state.cancellationArmed ? "ARMADO" : "DESARMADO"}
          </strong>
        </div>

        <div className="fiscal-certification-status-card">
          <span>Drafts</span>
          <strong>{state.drafts.length}</strong>
        </div>

        <div className="fiscal-certification-status-card">
          <span>Documentos</span>
          <strong>{state.documents.length}</strong>
        </div>
      </section>

      <section className="fiscal-certification-panel fiscal-certification-authorizations">
        <div className="fiscal-certification-panel__header">
          <div>
            <h2>Autorizações de operação</h2>
            <p>
              Cada autorização pertence a este tester, dura no
              máximo 5 minutos e é consumida pela próxima operação.
            </p>
          </div>
          <ShieldCheck size={21} />
        </div>

        <div className="fiscal-certification-subpanel">
          <div className="fiscal-certification-inline-status">
            Emissão:{" "}
            <strong>
              {state.issuanceArmed ? "ARMADA" : "DESARMADA"}
            </strong>

            {state.operationAuthorizations?.issue?.source && (
              <>
                {" · "}
                {state.operationAuthorizations.issue.source}
              </>
            )}

            {state.operationAuthorizations?.issue?.expiresAt && (
              <>
                {" · expira "}
                {formatDate(
                  state.operationAuthorizations.issue.expiresAt
                )}
              </>
            )}
          </div>

          <div className="fiscal-certification-actions">
            {!state.issuanceArmed ? (
              <button
                type="button"
                className="is-primary"
                onClick={() =>
                  authorizeNextCertificationOperation("ISSUE")
                }
                disabled={Boolean(busyAction)}
              >
                {busyAction === "authorize-issue"
                  ? "A autorizar…"
                  : "Autorizar próxima emissão · 5 min"}
              </button>
            ) : state.operationAuthorizations?.issue?.source ===
              "TESTER_ONE_SHOT" ? (
              <button
                type="button"
                onClick={() =>
                  revokeCertificationOperation("ISSUE")
                }
                disabled={Boolean(busyAction)}
              >
                {busyAction === "revoke-issue"
                  ? "A desarmar…"
                  : "Desarmar emissão"}
              </button>
            ) : null}
          </div>

          <div className="fiscal-certification-inline-status">
            Cancelamento:{" "}
            <strong>
              {state.cancellationArmed ? "ARMADO" : "DESARMADO"}
            </strong>

            {state.operationAuthorizations?.cancel?.source && (
              <>
                {" · "}
                {state.operationAuthorizations.cancel.source}
              </>
            )}

            {state.operationAuthorizations?.cancel?.expiresAt && (
              <>
                {" · expira "}
                {formatDate(
                  state.operationAuthorizations.cancel.expiresAt
                )}
              </>
            )}
          </div>

          <div className="fiscal-certification-actions">
            {!state.cancellationArmed ? (
              <button
                type="button"
                className="is-danger"
                onClick={() =>
                  authorizeNextCertificationOperation("CANCEL")
                }
                disabled={Boolean(busyAction)}
              >
                {busyAction === "authorize-cancel"
                  ? "A autorizar…"
                  : "Autorizar próximo cancelamento · 5 min"}
              </button>
            ) : state.operationAuthorizations?.cancel?.source ===
              "TESTER_ONE_SHOT" ? (
              <button
                type="button"
                onClick={() =>
                  revokeCertificationOperation("CANCEL")
                }
                disabled={Boolean(busyAction)}
              >
                {busyAction === "revoke-cancel"
                  ? "A desarmar…"
                  : "Desarmar cancelamento"}
              </button>
            ) : null}
          </div>

          <div className="fiscal-certification-hint">
            A autorização não cria latches no servidor. É específica
            deste tester, one-shot e desaparece também se o gateway
            reiniciar.
          </div>
        </div>
      </section>

      <section className="fiscal-certification-panel">
        <div className="fiscal-certification-panel__header">
          <div>
            <h2>Criar DRAFT de formação</h2>
            <p>
              É utilizado um fixture sintético já validado do mesmo
              tipo como template.
            </p>
          </div>
          <FileText size={21} />
        </div>

        <form
          className="fiscal-certification-form"
          onSubmit={createDraft}
        >
          <div className="fiscal-certification-form-grid fiscal-certification-draft-grid">
            <label>
              <span>Tipo</span>
              <select
                value={newDocumentType}
                onChange={event =>
                  setNewDocumentType(event.target.value)
                }
              >
                {DOCUMENT_TYPES.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </label>

            <label>
              <span>Data</span>
              <input
                type="date"
                min="2026-01-01"
                max="2026-12-31"
                value={issueDate}
                onChange={event =>
                  setIssueDate(event.target.value)
                }
              />
            </label>

            <label>
              <span>Descrição da primeira linha</span>
              <input
                type="text"
                value={newDescription}
                onChange={event =>
                  setNewDescription(event.target.value)
                }
              />
            </label>

            {isRectification && (
              <>
                <label>
                  <span>Documento original</span>
                  <select
                    value={originalDocumentId}
                    onChange={event =>
                      setOriginalDocumentId(event.target.value)
                    }
                  >
                    <option value="">Selecionar…</option>
                    {originalCandidates.map(document => (
                      <option
                        key={document.id}
                        value={document.id}
                      >
                        {documentType(document)} —{" "}
                        {documentNumber(document)}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="is-wide">
                  <span>Motivo da retificação</span>
                  <input
                    type="text"
                    value={rectificationReason}
                    onChange={event =>
                      setRectificationReason(event.target.value)
                    }
                  />
                </label>
              </>
            )}
          </div>

          <div className="fiscal-certification-inline-status">
            Template:{" "}
            <strong>
              {currentTemplate
                ? currentTemplate.id
                : `nenhum ${newDocumentType}`}
            </strong>
          </div>

          <div className="fiscal-certification-actions">
            <button
              type="submit"
              className="is-primary"
              disabled={
                loading ||
                Boolean(busyAction) ||
                !currentTemplate
              }
            >
              {busyAction === "create"
                ? "A criar…"
                : `Criar DRAFT ${newDocumentType}`}
            </button>
          </div>
        </form>
      </section>

      <section className="fiscal-certification-panel">
        <div className="fiscal-certification-panel__header">
          <div>
            <h2>Drafts de formação</h2>
            <p>
              Selecione um DRAFT para editar a descrição ou emitir.
            </p>
          </div>
          <FileText size={21} />
        </div>

        {loading ? (
          <div className="fiscal-certification-empty">
            A carregar…
          </div>
        ) : state.drafts.length === 0 ? (
          <div className="fiscal-certification-empty">
            Ainda não existem drafts.
          </div>
        ) : (
          <div className="fiscal-certification-table-wrap">
            <table className="fiscal-certification-table">
              <thead>
                <tr>
                  <th />
                  <th>ID</th>
                  <th>Tipo</th>
                  <th>Descrição</th>
                  <th>Estado</th>
                  <th>Atualizado</th>
                </tr>
              </thead>

              <tbody>
                {state.drafts
                  .slice()
                  .reverse()
                  .map(draft => (
                    <tr
                      key={draft.id}
                      className={
                        String(selectedDraftId) === String(draft.id)
                          ? "is-selected"
                          : ""
                      }
                      onClick={() =>
                        setSelectedDraftId(draft.id)
                      }
                    >
                      <td>
                        <input
                          type="radio"
                          name="certification-draft"
                          checked={
                            String(selectedDraftId) ===
                            String(draft.id)
                          }
                          onChange={() =>
                            setSelectedDraftId(draft.id)
                          }
                        />
                      </td>

                      <td className="is-mono">{draft.id}</td>
                      <td>
                        {draftDocumentType(draft) || "—"}
                      </td>
                      <td>{draftDescription(draft)}</td>
                      <td>
                        <span
                          className={statusPillClass(
                            draft.status || "DRAFT"
                          )}
                        >
                          {draft.status || "DRAFT"}
                        </span>
                      </td>
                      <td>{formatDate(draft.updatedAt)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="fiscal-certification-subpanel fiscal-certification-draft-editor">
          <label className="fiscal-certification-field">
            <span>Descrição do DRAFT selecionado</span>
            <input
              type="text"
              value={editDescription}
              disabled={!selectedDraftIsEditable}
              onChange={event =>
                setEditDescription(event.target.value)
              }
            />
          </label>

          <div className="fiscal-certification-actions">
            <button
              type="button"
              onClick={saveDraftDescription}
              disabled={
                !selectedDraftIsEditable ||
                Boolean(busyAction)
              }
            >
              {busyAction === "edit"
                ? "A guardar…"
                : "Guardar descrição"}
            </button>

            <button
              type="button"
              className="is-primary"
              onClick={issueSelectedDraft}
              disabled={
                !selectedDraftIsEditable ||
                !state.issuanceArmed ||
                Boolean(busyAction)
              }
            >
              {busyAction === "issue"
                ? "A emitir…"
                : "Emitir DRAFT"}
            </button>
          </div>

          {!state.issuanceArmed && (
            <div className="fiscal-certification-hint">
              Emissão desarmada: pode preparar e editar drafts,
              mas nenhum documento fiscal será emitido.
            </div>
          )}
        </div>
      </section>

      <section className="fiscal-certification-panel">
        <div className="fiscal-certification-panel__header">
          <div>
            <h2>Documentos emitidos</h2>
            <p>
              Selecione um documento para reimprimir PDF ou
              executar cancelamento controlado.
            </p>
          </div>
          <FileCheck2 size={21} />
        </div>

        {state.documents.length === 0 ? (
          <div className="fiscal-certification-empty">
            Ainda não existem documentos emitidos.
          </div>
        ) : (
          <div className="fiscal-certification-table-wrap">
            <table className="fiscal-certification-table">
              <thead>
                <tr>
                  <th />
                  <th>ID</th>
                  <th>Tipo</th>
                  <th>Número</th>
                  <th>Estado</th>
                  <th>ATCUD</th>
                  <th>Hash</th>
                </tr>
              </thead>

              <tbody>
                {state.documents.map(document => (
                  <tr
                    key={document.id}
                    className={
                      String(selectedDocumentId) ===
                      String(document.id)
                        ? "is-selected"
                        : ""
                    }
                    onClick={() =>
                      setSelectedDocumentId(document.id)
                    }
                  >
                    <td>
                      <input
                        type="radio"
                        name="certification-document"
                        checked={
                          String(selectedDocumentId) ===
                          String(document.id)
                        }
                        onChange={() =>
                          setSelectedDocumentId(document.id)
                        }
                      />
                    </td>

                    <td className="is-mono">{document.id}</td>
                    <td>{documentType(document) || "—"}</td>
                    <td className="is-mono">
                      {documentNumber(document)}
                    </td>

                    <td>
                      <span
                        className={statusPillClass(
                          documentStatus(document)
                        )}
                      >
                        {documentStatus(document)}
                      </span>
                    </td>

                    <td className="is-mono is-wrap">
                      {documentAtcud(document)}
                    </td>

                    <td
                      className="is-mono is-wrap"
                      title={documentHash(document)}
                    >
                      {documentHash(document)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="fiscal-certification-subpanel fiscal-certification-document-actions">
          <div className="fiscal-certification-document-actions__heading">
            <strong>Ações do documento</strong>
          </div>

          <div className="fiscal-certification-actions fiscal-certification-document-actions__pdf">
            <button
              type="button"
              onClick={openSelectedPdf}
              disabled={
                !selectedDocument || Boolean(busyAction)
              }
            >
              {busyAction === "pdf"
                ? "A preparar PDF…"
                : "Reimprimir / abrir PDF"}
            </button>
          </div>

          <div className="fiscal-certification-form-grid">
            <label className="is-wide">
              <span>Motivo de cancelamento</span>
              <input
                type="text"
                value={cancellationReason}
                onChange={event =>
                  setCancellationReason(event.target.value)
                }
              />
            </label>

            <label className="fiscal-certification-checkbox is-wide">
              <input
                type="checkbox"
                checked={originalNotDelivered}
                onChange={event =>
                  setOriginalNotDelivered(event.target.checked)
                }
              />
              <span>
                Confirmo que o original não foi entregue ao cliente.
              </span>
            </label>
          </div>

          <div className="fiscal-certification-actions fiscal-certification-document-actions__cancel">
            <button
              type="button"
              className="is-danger"
              onClick={cancelSelectedDocument}
              disabled={
                !selectedDocument ||
                selectedDocumentIsCancelled ||
                !state.cancellationArmed ||
                !originalNotDelivered ||
                Boolean(busyAction)
              }
            >
              {busyAction === "cancel"
                ? "A cancelar…"
                : "Cancelar documento"}
            </button>
          </div>

          {!state.cancellationArmed && (
            <div className="fiscal-certification-hint">
              Cancelamento desarmado: autorize explicitamente
              o próximo cancelamento antes de executar a operação.
            </div>
          )}
        </div>

        <div className="fiscal-certification-hint">
          ATCUD e hash são apresentados nesta tabela quando
          fornecidos pela API. O QR é observado no PDF fiscal.
        </div>
      </section>

      <section className="fiscal-certification-panel fiscal-certification-saft">
        <div className="fiscal-certification-panel__header">
          <div>
            <h2>Exportar SAF-T</h2>
            <p>
              Exportação fiscal da company sintética AT,
              no período acumulado até à data indicada.
            </p>
          </div>
          <FileCheck2 size={21} />
        </div>

        <div className="fiscal-certification-form-grid">
          <label>
            <span>Data final</span>
            <input
              type="date"
              min="2026-01-01"
              max="2026-12-31"
              value={saftDate}
              onChange={event =>
                setSaftDate(event.target.value)
              }
            />
          </label>
        </div>

        <div className="fiscal-certification-actions">
          <button
            type="button"
            className="is-primary"
            onClick={exportSaft}
            disabled={!saftDate || Boolean(busyAction)}
          >
            {busyAction === "saft"
              ? "A gerar SAF-T…"
              : "Exportar SAF-T"}
          </button>
        </div>
      </section>

      <div className="fiscal-certification-operation-state">
        Gateway:{" "}
        <strong>
          {state.fiscalDocumentOperations || "—"}
        </strong>

        {" · "}Company fixa: <strong>910001</strong>
        {" · "}API: <strong>TEST</strong>
      </div>
    </div>
  );
}
